import { useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ArrowRightLeft } from 'lucide-react';
import { RouteTaskDialog } from './DeptTaskShared';

/**
 * Violation reports list — each row has an inline "Route to…" quick
 * action that pre-fills the RouteTaskDialog with a subject built from
 * the violation type + property + unit, so admins don't retype context
 * when escalating a violation to Safety, Legal, or Customer Service.
 */
export default function AdminViolationReportsTab({ allViolations }) {
  const [routeCtx, setRouteCtx] = useState(null);

  return (
    <Card className="bg-zinc-900 p-6">
      <h2 className="text-xl font-bold mb-4 text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        ALL VIOLATION REPORTS
      </h2>

      <div className="space-y-3">
        {allViolations.length === 0 ? (
          <p className="text-zinc-400 text-center py-8">No violation reports</p>
        ) : (
          allViolations.map((violation) => {
            const suggestedDept = ['safety_hazard', 'fire_hazard', 'blocked_exit'].includes(violation.violation_type)
              ? 'safety'
              : ['legal', 'lawsuit', 'liability'].some((k) => (violation.description || '').toLowerCase().includes(k))
                ? 'legal'
                : 'customer-service';
            const preSubject = `Violation escalation · ${violation.violation_type} at ${violation.property_name} · Unit ${violation.unit_number}`;
            const preDetail = `${violation.description || ''}\n\nReporter: ${violation.reporter_name}\nFiled: ${new Date(violation.created_at).toLocaleString()}\nStatus: ${violation.status}`;
            return (
              <Card key={violation.violation_id} className="bg-zinc-800 border-zinc-700 p-4">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2 flex-wrap">
                      <Badge className="bg-amber-500 text-black">{violation.violation_type}</Badge>
                      <Badge
                        className={
                          violation.status === 'pending_company_review' ? 'bg-red-500'
                            : violation.status === 'active' ? 'bg-blue-500'
                            : violation.status === 'resolved' ? 'bg-green-500'
                            : violation.status === 'rejected' ? 'bg-zinc-600'
                            : 'bg-zinc-500'
                        }
                      >
                        {violation.status.replace('_', ' ')}
                      </Badge>
                      <Badge className={violation.source === 'tenant' ? 'bg-amber-700' : 'bg-purple-700'}>
                        {violation.source}
                      </Badge>
                    </div>
                    <h3 className="font-bold text-white">
                      {violation.property_name} - Unit {violation.unit_number}
                    </h3>
                    <p className="text-sm text-zinc-300">{violation.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-zinc-500 mt-2">
                      <span>Reporter: {violation.reporter_name}</span>
                      <span>{new Date(violation.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setRouteCtx({ subject: preSubject, detail: preDetail, dept: suggestedDept, related: `${violation.property_name} · Unit ${violation.unit_number}` })}
                    className="bg-[#C9A962] text-black hover:bg-[#EAB308] text-xs h-8"
                    data-testid={`violation-route-${violation.violation_id}`}
                  >
                    <ArrowRightLeft className="h-3 w-3 mr-1" /> Route to…
                  </Button>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <RouteTaskDialog
        open={!!routeCtx}
        onClose={() => setRouteCtx(null)}
        defaultDept={routeCtx?.dept || 'safety'}
        onSaved={() => setRouteCtx(null)}
        prefill={routeCtx || undefined}
      />
    </Card>
  );
}
